import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useThemeStore } from "../../store/theme";
import { useNavigationState } from "../../hooks/useNavigationState";
import {
  Settings,
  User,
  Search,
  Sparkle,
  HelpCircle,
  CheckSquare,
  LogOut,
  Globe,
  Loader2,
  Coffee,
  Inbox,
} from "lucide-react";

// Mock or existing app store hooks if you only need isLoggedIn/logout here
import { useAppStore } from "../../store";

const LeftSidebar: React.FC = () => {
  const { colors, theme } = useThemeStore();
  const { isLoggedIn, logout } = useAppStore();
  const location = useLocation();
  const navigate = useNavigate();
  const isNavigating = useNavigationState();

  // Local state for detected region and loading status
  const [detectedRegion, setDetectedRegion] = useState<string | null>(null);
  const [locating, setLocating] = useState<boolean>(false);
  const hasInitialized = useRef<boolean>(false);

  // Self-contained location detection logic
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    if (!navigator.geolocation) return;

    setLocating(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await res.json();

          if (data?.address) {
            const city =
              data.address.town ||
              data.address.city ||
              data.address.village ||
              data.address.suburb ||
              "";
            const state = data.address.state || data.address.county || "";
            const country = data.address.country || "";

            const regionString = [city, state, country]
              .filter(Boolean)
              .join(", ");

            if (regionString) {
              setDetectedRegion(regionString);
            }
          }
        } catch (err) {
          console.error("Reverse geocoding error:", err);
        } finally {
          setLocating(false);
        }
      },
      (error) => {
        console.warn("Geolocation error:", error.message);
        setLocating(false);
      },
      { timeout: 8000, enableHighAccuracy: true }
    );
  }, []);

  const navigationItems = [
    { path: "/", icon: Search, label: "Discover", exact: true },
    { path: "/community", icon: Coffee, label: "The Café" },
    { path: "/inbox", icon: Inbox, label: "Inbox" },
    { path: "/checklists", icon: CheckSquare, label: "Checklists" },
    { path: "/llm", icon: Sparkle, label: "Spark Co-Pilot" },
    ...(isLoggedIn ? [{ path: "/profile", icon: User, label: "Profile" }] : []),
    { path: "/settings", icon: Settings, label: "Settings" },
  ];

  const handleNavigation = (path: string) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  return (
    <aside
      className="w-64 border-r pt-4 pb-6 px-4 fixed left-0 top-16 h-full overflow-y-auto"
      style={{
        backgroundColor: colors.background,
        borderColor: theme === "light" ? "#e5e7eb" : "#374151",
      }}
    >
      <nav className="space-y-2">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.exact
            ? location.pathname === item.path
            : location.pathname.startsWith(item.path);

          return (
            <button
              key={item.path}
              id={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
              onClick={() => handleNavigation(item.path)}
              className={`idearify-nav-item ${isActive ? "idearify-nav-item active" : ""} ${isNavigating ? "opacity-50" : ""}`}
              style={{
                color: isActive ? colors.primary : colors.text,
                backgroundColor: "transparent",
                border: "none",
                width: "100%",
                textAlign: "left",
              }}
              disabled={isNavigating}
            >
              <Icon
                size={24}
                style={{ color: isActive ? colors.primary : colors.gray }}
              />
              <span className="text-sm font-medium">{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Divider */}
      <div
        className="border-t my-6"
        style={{ borderColor: theme === "light" ? "#e5e7eb" : "#374151" }}
      ></div>

      <div className="flex flex-col h-1/2 justify-between">
        {/* Region & Navigation / Status Section */}
        <div className="space-y-2">
          {/* State 1: Locating */}
          {locating && (
            <div
              className="flex items-center gap-2 px-3 py-2 text-xs rounded-md"
              style={{ color: colors.gray }}
            >
              <Loader2 size={16} className="animate-spin shrink-0" />
              <span>Detecting region...</span>
            </div>
          )}

          {/* State 2: Detected */}
          {!locating && detectedRegion && (
            <div
              className="flex items-center gap-2 px-3 py-2 text-xs rounded-md"
              style={{ color: colors.gray }}
              title={detectedRegion}
            >
              <Globe size={16} className="shrink-0" />
              <span>{detectedRegion}</span>
            </div>
          )}

          {/* Help */}
          <button
            onClick={() => {
              navigate("/help");
            }}
            type="button"
            className="group flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 cursor-pointer"
            style={{ color: colors.primary }}
          >
            <HelpCircle
              size={18}
              className="shrink-0 transition-transform group-hover:-translate-x-0.5"
              style={{ color: colors.primary }}
            />
            <span>
              Help Center
            </span>
          </button>

          {/* Restyled Log Out Action */}
          {isLoggedIn && (
            <button
              onClick={() => {
                logout();
                navigate("/login");
              }}
              type="button"
              className="group flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-red-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 cursor-pointer"
              style={{ color: colors.error || "#ef4444" }}
            >
              <LogOut
                size={18}
                className="shrink-0 transition-transform group-hover:-translate-x-0.5"
                style={{ color: colors.error || "#ef4444" }}
              />
              <span>Log Out</span>
            </button>
          )}
        </div>

        {/* Additional Links Pinned to the Bottom */}
        <div
          className="mt-auto pt-6 border-t space-y-2 text-xs"
          style={{
            borderColor: theme === "light" ? "#f3f4f6" : "#27272a",
            color: colors.gray,
          }}
        >
          <div className="flex flex-wrap items-center gap-2">
            <a href="https://idearify.com/company" className="hover:underline transition-opacity">
              About
            </a>
            <span>•</span>
            <a href="https://idearify.com/help" className="hover:underline transition-opacity">
              Contact
            </a>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <a href="https://idearify.com/legal#terms" className="hover:underline transition-opacity">
              Terms
            </a>
            <span>•</span>
            <a href="https://idearify.com/legal#privacy-policy" className="hover:underline transition-opacity">
              Privacy
            </a>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <a href="https://idearify.com/legal#community-guidelines" className="hover:underline transition-opacity">
              Community Guidelines
            </a>
          </div>

          <p
            className="pt-2 text-[11px]"
            style={{ color: theme === "light" ? "#9ca3af" : "#6b7280" }}
          >
            Idearify © 2026. All rights reserved.
          </p>
          <p
            className="text-[11px]"
            style={{ color: theme === "light" ? "#9ca3af" : "#6b7280" }}
          >
            Idearify is currently operated by DNA Entertainment Ltd, acting as an
            interim holding company for Idearify (company no. 13784763).
          </p>
        </div>
      </div>
    </aside>
  );
};

export default LeftSidebar;